import { Group, Text, ThemeIcon } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconMinus,
} from "@tabler/icons-react";

/**
 * One vocabulary for "this number moved", shared by every widget that draws an arrow.
 *
 * Written because five components had five copies of the same twelve lines, with three different
 * palettes (teal/green/red), three different roundings, and no agreement on whether the sign belongs
 * in the text or in the icon. Two of the copies were also wrong:
 *
 *   - KeyMetricsWidget hid the whole trend row whenever |change| >= 100%, so the biggest movements
 *     were the only ones nobody saw. It was trying to suppress absurd percentages off tiny
 *     baselines; it suppressed the signal instead.
 *   - BrandActivityTimeline appended "%" to every event's metricValue, including the awareness
 *     events whose value is a change in RANK PLACES. "position improved (+4%)" is not a thing.
 *
 * The one non-obvious rule: the arrow and the colour both mean BETTER or WORSE, never up or down.
 * Search position 9 beats position 14, so a falling number gets a green up-arrow. That is the same
 * fact `MetricDirection` encodes on the backend, and the same one the email renderer reads — keep
 * the three in step. The keys below deliberately match the backend's metric keys.
 */

/** unit: how to read the delta value itself, not the metric it came from. */
export const METRICS = {
  // Traffic and counts. Their deltas arrive already expressed as a percentage change.
  events: { label: "Events", lowerIsBetter: false, unit: "percent" },
  pageviews: { label: "Pageviews", lowerIsBetter: false, unit: "percent" },
  visitors: { label: "Visitors", lowerIsBetter: false, unit: "percent" },
  sessions: { label: "Sessions", lowerIsBetter: false, unit: "percent" },
  clicks: { label: "Clicks", lowerIsBetter: false, unit: "percent" },
  impressions: { label: "Impressions", lowerIsBetter: false, unit: "percent" },
  mentions: { label: "Mentions", lowerIsBetter: false, unit: "percent" },

  // Rates in 0..1. A relative change on a rate reads as nonsense — "share of voice +158%" — so
  // their deltas are shown in percentage points.
  shareOfVoice: { label: "Share of voice", lowerIsBetter: false, unit: "points" },
  recommendationRate: { label: "Recommendation rate", lowerIsBetter: false, unit: "points" },
  mentionRate: { label: "Mention rate", lowerIsBetter: false, unit: "points" },

  // Ranks. Lower is better, and their deltas are counted in places.
  averagePosition: { label: "Avg. position", lowerIsBetter: true, unit: "places" },
  averageRank: { label: "Avg. rank", lowerIsBetter: true, unit: "places" },
  mentionRank: { label: "Mention rank", lowerIsBetter: true, unit: "places" },
};

/**
 * The smallest baseline a percentage may be computed against.
 *
 * Matches MetricDescriptor.MinBaselineForPercentage on the backend, for the same reason: a period
 * going from 1 pageview to 30 is +2,900% and tells the reader only that the baseline was 1. Note the
 * analytics API reports a first-ever period as exactly +100% (CalculatePercentageChange returns 100
 * when the old value is 0), which is not a real figure either — passing `baseline` is what lets this
 * component ignore it.
 */
export const MIN_BASELINE_FOR_PERCENTAGE = 10;

/** Above this multiple, "×3.4" beats "+240%" for legibility. */
const MULTIPLE_THRESHOLD_PERCENT = 200;

function formatDelta(value, unit) {
  const abs = Math.abs(value);
  const sign = value > 0 ? "+" : "−";

  if (unit === "points") return `${sign}${(abs * 100).toFixed(1)}pp`;
  if (unit === "places") return `${sign}${abs.toFixed(1)}`;

  if (value >= MULTIPLE_THRESHOLD_PERCENT) return `×${(1 + value / 100).toFixed(1)}`;
  return `${sign}${abs < 1 ? abs.toFixed(1) : Math.round(abs)}%`;
}

/**
 * Renders how a metric moved: an arrow, a colour and the change.
 *
 * @param metric   key into METRICS. An unknown key is treated as higher-is-better percent.
 * @param value    the delta. Percent for counts, 0..1 for rates, places for ranks.
 * @param baseline the previous-period value, when the caller has it. Supplying it is what allows
 *                 "was 3" instead of a percentage that cannot mean anything.
 * @param hideWhenFlat drop the row entirely on no change, rather than showing a dash.
 * @param context trailing words for the comparison, e.g. "vs prev". Rendered ONLY beside an actual
 *                delta — a caller that printed its own static label ended up with "was 3 vs prev".
 */
export function MetricDelta({
  metric,
  value,
  baseline,
  size = "xs",
  hideWhenFlat = false,
  context,
}) {
  const spec = METRICS[metric] ?? { lowerIsBetter: false, unit: "percent" };

  if (value == null) return null;

  // No usable baseline: name it instead of dividing by it. Silence would be worse — "30" with
  // nothing beside it reads as a collapse just as easily as a first week.
  //
  // Ahead of the isFinite guard deliberately. A baseline of 0 divides to Infinity, and "was 0" is
  // the honest reading of that; checking isFinite first would drop the row and say nothing at all.
  if (spec.unit === "percent" && baseline != null && Math.abs(baseline) < MIN_BASELINE_FOR_PERCENTAGE) {
    return (
      <Text size={size} c="dimmed">
        was {baseline.toLocaleString()}
      </Text>
    );
  }

  if (!isFinite(value)) return null;

  if (value === 0) {
    if (hideWhenFlat) return null;
    return (
      <Group gap={2} wrap="nowrap">
        <ThemeIcon size={14} radius="xl" variant="light" color="gray">
          <IconMinus size={10} />
        </ThemeIcon>
        <Text size={size} c="dimmed" fw={500}>
          no change
        </Text>
      </Group>
    );
  }

  // Better or worse, never up or down. This is the whole point of the module.
  const better = spec.lowerIsBetter ? value < 0 : value > 0;
  const color = better ? "teal" : "red";
  const Icon = better ? IconArrowUp : IconArrowDown;

  return (
    <Group gap={2} wrap="nowrap">
      <ThemeIcon size={14} radius="xl" variant="light" color={color}>
        <Icon size={10} />
      </ThemeIcon>
      <Text size={size} c={color} fw={500}>
        {formatDelta(value, spec.unit)}
      </Text>
      {context && (
        <Text size={size} c="dimmed">
          {context}
        </Text>
      )}
    </Group>
  );
}
